import {
  CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS,
  CHAT_ATTACHMENTS_ALLOWED_MIME_TYPES,
  normalizePastedFile,
} from '@heidi/utils';
import { useCallback } from 'react';

type UseChatPasteOptions = {
  /**
   * Callback to add files when paste contains valid attachments.
   * Typically comes from useChatFileUpload.addFiles.
   */
  onFilesDetected: (files: File[]) => void;
  /**
   * Whether paste handling is enabled.
   * Should be tied to IS_CHAT_ATTACHMENTS_ENABLED feature flag.
   */
  enabled?: boolean;
};

/**
 * Validates if a file has an allowed extension for chat attachments.
 * Uses the centralized CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS set.
 */
const isFileAllowed = (file: File): boolean => {
  // Check if filename has an extension (contains a dot that's not at the start)
  const lastDotIndex = file.name.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;

  if (!hasExtension) {
    // Extensionless clipboard files must match the explicit MIME allow-list.
    return CHAT_ATTACHMENTS_ALLOWED_MIME_TYPES.has(file.type.toLowerCase());
  }

  const extension = file.name.slice(lastDotIndex + 1).toLowerCase();
  return CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS.has(extension);
};

/**
 * Hook for handling paste events in the chat input.
 * Extracts files from clipboard and passes them to the file upload handler.
 *
 * When files are detected in the paste event:
 * - Files are validated against allowed extensions
 * - Valid files are passed to onFilesDetected callback
 * - The paste event is prevented to avoid inserting file data as text
 *
 * When only text is pasted:
 * - The event is not intercepted
 * - TipTap handles the text paste normally
 */
export const useChatPaste = ({
  onFilesDetected,
  enabled = true,
}: UseChatPasteOptions) => {
  const handlePaste = useCallback(
    (event: ClipboardEvent): boolean => {
      if (!enabled) return false;

      const items = event.clipboardData?.items;
      if (!items) return false;

      const files: File[] = [];

      Array.from(items).forEach((item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file && isFileAllowed(file)) {
            files.push(normalizePastedFile(file));
          }
        }
      });

      if (files.length > 0) {
        // Prevent default to avoid inserting file data as text
        event.preventDefault();
        event.stopPropagation();
        onFilesDetected(files);
        return true;
      }
      // If no files, let the event propagate for normal text paste handling
      return false;
    },
    [enabled, onFilesDetected]
  );

  return { handlePaste };
};
